import { useState } from 'react';

import {
  Fieldset,
  Label,
  RadioButtonField,
  Typography,
} from '@remarkable/ark-web';

import { Padlock } from '@remarkable/ark-icons';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import type { PolicyAutoLockMinutesValues } from 'src/api/endpoints/cloudEnterprisePolicyApi.types';
import { useSendCommandSetAutolockTimeout } from 'src/api/queries/enterprisePolicies';
import { Button, Modal, NotificationBox } from 'src/components';
import { DeviceName } from 'src/components/DeviceName';

export function DeviceActionSetAutolockTimeout({
  device,
  userId,
  onSuccess,
}: {
  device: FleetDevice;
  userId: string;
  onSuccess?: () => void;
}) {
  const sendCommandSetAutolockTimeout = useSendCommandSetAutolockTimeout();
  const [autoLockMinutes, setAutoLockMinutes] =
    useState<PolicyAutoLockMinutesValues>(0);
  const autoLockValues = [0, 60, 20, 5] satisfies PolicyAutoLockMinutesValues[];

  return (
    <Modal.Content>
      <div className="flex justify-center">
        <Modal.Title asChild>
          <Typography
            variant="responsive-heading-md"
            className="flex items-center gap-8"
            as="h2"
          >
            <Padlock size={32} className="inline" /> Auto-lock
          </Typography>
        </Modal.Title>
      </div>
      <div className="mt-24 flex flex-col gap-24">
        <Modal.Description asChild>
          <Typography variant="body-md">
            Enforce automatic device lock and disable the use of “light sleep”
            mode for enhanced security.
          </Typography>
        </Modal.Description>

        <div className="rounded-sm bg-paper-3 p-16">
          <Typography variant="body-md">
            <b>
              <DeviceName device={device} />
            </b>{' '}
            <span className="whitespace-nowrap">({device.serialNumber})</span>
          </Typography>
        </div>

        <div className="flex flex-col gap-12 py-16">
          <Label>Autolock timeout</Label>
          <Fieldset className="grid grid-cols-2 items-center justify-center gap-12 lm:flex">
            {autoLockValues.map((option) => (
              <RadioButtonField
                key={option}
                name="autoLockMinutes"
                label={option === 0 ? 'None' : `After ${option} min`}
                value={option}
                checked={autoLockMinutes === option}
                onChange={() => {
                  setAutoLockMinutes(option);
                }}
              />
            ))}
          </Fieldset>
        </div>
      </div>

      <div className="mt-24 flex justify-center gap-16">
        <Modal.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Modal.Close>

        <Button
          variant="primary"
          loading={sendCommandSetAutolockTimeout.isPending}
          onClick={() => {
            sendCommandSetAutolockTimeout.mutate(
              {
                deviceId: device.serialNumber,
                userId,
                timeout: autoLockMinutes,
              },
              { onSuccess }
            );
          }}
        >
          Send
        </Button>
      </div>

      <NotificationBox
        error={sendCommandSetAutolockTimeout.error}
        className="mt-16"
      />
    </Modal.Content>
  );
}
